import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { formatCurrency, formatDate } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  ArrowLeft,
  Calendar,
  CheckCircle2,
  Clock,
  DollarSign,
  Edit,
  ExternalLink,
  Flame,
  ListTodo,
  Pencil,
  Shield,
  Trash2,
  User,
  Users,
  Building2,
  Briefcase,
  FolderKanban,
} from 'lucide-react';

// --- Types ---

interface Project {
  id: string;
  projectNumber: number;
  name: string;
  description: string | null;
  status: string;
  projectType: string;
  billingType: string;
  budgetAmount: number | null;
  budgetHours: number | null;
  startDate: string | null;
  targetEndDate: string | null;
  actualEndDate: string | null;
  completionPct: number;
  accountId: string | null;
  accountName: string | null;
  projectManagerId: string | null;
  projectManagerName: string | null;
  totalLoggedHours: number;
  billableHours: number;
  billableAmount: number;
  taskCount: number;
  completedTaskCount: number;
}

interface Phase {
  id: string;
  name: string;
  description: string | null;
  status: string;
  sortOrder: number;
  startDate: string | null;
  endDate: string | null;
  completionPct: number;
  milestoneDate: string | null;
}

interface ResourceAssignment {
  id: string;
  userId: string;
  userName: string;
  role: string;
  allocationPct: number;
  startDate: string | null;
  endDate: string | null;
  billableRate: number | null;
}

// --- Status Badge Variants ---

const statusVariant: Record<string, 'default' | 'success' | 'warning' | 'destructive' | 'secondary' | 'info'> = {
  planning: 'secondary',
  active: 'success',
  on_hold: 'warning',
  completed: 'info',
  cancelled: 'destructive',
  archived: 'secondary',
};

const phaseStatusVariant: Record<string, 'default' | 'success' | 'secondary' | 'info'> = {
  not_started: 'secondary',
  in_progress: 'info',
  completed: 'success',
  skipped: 'secondary',
};

// --- Progress Bar Component ---

function ProgressBar({ value, className = '' }: { value: number; className?: string }) {
  const clamped = Math.min(100, Math.max(0, value));
  return (
    <div className={`relative h-2 w-full overflow-hidden rounded-full bg-secondary ${className}`}>
      <div
        className="h-full bg-primary rounded-full transition-all"
        style={{ width: `${clamped}%` }}
      />
    </div>
  );
}

// --- Main Component ---

export function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // --- Queries ---

  const {
    data: projectData,
    isLoading: projectLoading,
    error: projectError,
  } = useQuery({
    queryKey: ['project', id],
    queryFn: async () => {
      const result = await api.getProject(id!);
      return result.data as Project;
    },
    enabled: !!id,
  });

  const { data: phasesData } = useQuery({
    queryKey: ['project-phases', id],
    queryFn: async () => {
      const result = await api.listPhases(id!);
      return (result.data || []) as Phase[];
    },
    enabled: !!id,
  });

  const { data: resourcesData } = useQuery({
    queryKey: ['project-resources', id],
    queryFn: async () => {
      const result = await api.listResourceAssignments(id!);
      return (result.data || []) as ResourceAssignment[];
    },
    enabled: !!id,
  });

  // --- Mutations ---

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const result = await api.deleteProject(id!);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to delete project');
      }
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['project-dashboard'] });
      navigate('/projects/list');
    },
  });

  // --- Derived Data ---

  const project = projectData;
  const phases = phasesData || [];
  const resources = resourcesData || [];

  const taskCompletionPct =
    project && project.taskCount > 0
      ? Math.round((project.completedTaskCount / project.taskCount) * 100)
      : 0;

  const budgetBurnedPct =
    project && project.budgetAmount && project.budgetAmount > 0
      ? Math.round((project.billableAmount / project.budgetAmount) * 100)
      : 0;

  // --- Loading & Error States ---

  if (projectLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (projectError || !project) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <p className="text-muted-foreground">Project not found or failed to load.</p>
        <Button variant="outline" onClick={() => navigate('/projects/list')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Projects
        </Button>
      </div>
    );
  }

  // --- Handlers ---

  function handleEdit() {
    navigate(`/projects/list/${id}/edit`);
  }

  function handleDelete() {
    setDeleteDialogOpen(true);
  }

  function confirmDelete() {
    deleteMutation.mutate();
  }

  // --- Render ---

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => navigate('/projects/list')}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <span className="font-mono text-sm text-muted-foreground">#{project.projectNumber}</span>
            <Badge variant={statusVariant[project.status] || 'secondary'}>
              {project.status.replace(/_/g, ' ')}
            </Badge>
          </div>
          <h1 className="text-2xl font-bold">{project.name}</h1>
          {project.description && (
            <p className="text-muted-foreground max-w-2xl">{project.description}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleEdit}>
            <Pencil className="h-4 w-4 mr-2" />
            Edit
          </Button>
          <Button variant="destructive" onClick={handleDelete}>
            <Trash2 className="h-4 w-4 mr-2" />
            Delete
          </Button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Task Completion</CardTitle>
            <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{taskCompletionPct}%</div>
            <p className="text-xs text-muted-foreground">
              {project.completedTaskCount} of {project.taskCount} tasks completed
            </p>
            <ProgressBar value={taskCompletionPct} className="mt-2" />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Hours Logged</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{project.totalLoggedHours.toFixed(1)}h</div>
            <p className="text-xs text-muted-foreground">
              {project.billableHours.toFixed(1)}h billable
              {project.budgetHours
                ? ` / ${project.budgetHours}h budgeted`
                : ''}
            </p>
            {project.budgetHours && project.budgetHours > 0 && (
              <ProgressBar
                value={Math.round((project.totalLoggedHours / project.budgetHours) * 100)}
                className="mt-2"
              />
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Budget Burned</CardTitle>
            <Flame className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {project.budgetAmount ? `${budgetBurnedPct}%` : 'N/A'}
            </div>
            <p className="text-xs text-muted-foreground">
              {formatCurrency(project.billableAmount)} spent
              {project.budgetAmount ? ` of ${formatCurrency(project.budgetAmount)}` : ''}
            </p>
            {project.budgetAmount && project.budgetAmount > 0 && (
              <ProgressBar value={budgetBurnedPct} className="mt-2" />
            )}
          </CardContent>
        </Card>
      </div>

      {/* Project Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center gap-2 space-y-0 pb-2">
            <Building2 className="h-4 w-4 text-muted-foreground" />
            <CardTitle className="text-sm font-medium">Client</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-lg font-semibold">{project.accountName || 'Internal Project'}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center gap-2 space-y-0 pb-2">
            <Briefcase className="h-4 w-4 text-muted-foreground" />
            <CardTitle className="text-sm font-medium">Type</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-lg font-semibold capitalize">{project.projectType.replace(/_/g, ' ')}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center gap-2 space-y-0 pb-2">
            <DollarSign className="h-4 w-4 text-muted-foreground" />
            <CardTitle className="text-sm font-medium">Billing</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-lg font-semibold capitalize">{project.billingType.replace(/_/g, ' ')}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center gap-2 space-y-0 pb-2">
            <DollarSign className="h-4 w-4 text-muted-foreground" />
            <CardTitle className="text-sm font-medium">Budget</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-lg font-semibold">
              {project.budgetAmount ? formatCurrency(project.budgetAmount) : 'No budget set'}
            </p>
            {project.budgetHours && (
              <p className="text-xs text-muted-foreground">{project.budgetHours}h budgeted</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center gap-2 space-y-0 pb-2">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <CardTitle className="text-sm font-medium">Dates</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1">
              <p className="text-sm">
                <span className="text-muted-foreground">Start:</span>{' '}
                {project.startDate ? formatDate(project.startDate) : 'Not set'}
              </p>
              <p className="text-sm">
                <span className="text-muted-foreground">Target End:</span>{' '}
                {project.targetEndDate ? formatDate(project.targetEndDate) : 'Not set'}
              </p>
              {project.actualEndDate && (
                <p className="text-sm">
                  <span className="text-muted-foreground">Actual End:</span>{' '}
                  {formatDate(project.actualEndDate)}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center gap-2 space-y-0 pb-2">
            <User className="h-4 w-4 text-muted-foreground" />
            <CardTitle className="text-sm font-medium">Manager</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-lg font-semibold">
              {project.projectManagerName || 'Unassigned'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Phases / Milestones */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FolderKanban className="h-5 w-5" />
            Phases & Milestones
          </CardTitle>
        </CardHeader>
        <CardContent>
          {phases.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">
              No phases defined for this project.
            </p>
          ) : (
            <div className="space-y-4">
              {phases
                .sort((a, b) => a.sortOrder - b.sortOrder)
                .map((phase, index) => (
                  <div key={phase.id} className="relative">
                    {/* Timeline connector */}
                    {index < phases.length - 1 && (
                      <div className="absolute left-3 top-8 bottom-0 w-px bg-border" />
                    )}
                    <div className="flex items-start gap-3">
                      {/* Timeline dot */}
                      <div
                        className={`mt-1 h-6 w-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                          phase.status === 'completed'
                            ? 'bg-emerald-100 border-emerald-500'
                            : phase.status === 'in_progress'
                            ? 'bg-sky-100 border-sky-500'
                            : 'bg-secondary border-border'
                        }`}
                      >
                        {phase.status === 'completed' && (
                          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                        )}
                        {phase.status === 'in_progress' && (
                          <div className="h-2 w-2 rounded-full bg-sky-500" />
                        )}
                      </div>

                      {/* Phase content */}
                      <div className="flex-1 pb-4">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{phase.name}</span>
                            <Badge variant={phaseStatusVariant[phase.status] || 'secondary'}>
                              {phase.status.replace(/_/g, ' ')}
                            </Badge>
                          </div>
                          <span className="text-sm text-muted-foreground">
                            {phase.completionPct.toFixed(0)}%
                          </span>
                        </div>
                        {phase.description && (
                          <p className="text-sm text-muted-foreground mb-2">{phase.description}</p>
                        )}
                        <ProgressBar value={phase.completionPct} className="mb-2" />
                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          {phase.startDate && <span>Start: {formatDate(phase.startDate)}</span>}
                          {phase.endDate && <span>End: {formatDate(phase.endDate)}</span>}
                          {phase.milestoneDate && (
                            <span className="font-medium text-primary">
                              Milestone: {formatDate(phase.milestoneDate)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Team Members / Resource Assignments */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Team Members
          </CardTitle>
        </CardHeader>
        <CardContent>
          {resources.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">
              No team members assigned to this project.
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {resources.map((resource) => (
                <div
                  key={resource.id}
                  className="flex items-center gap-3 p-3 rounded-lg border"
                >
                  <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <User className="h-4 w-4 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{resource.userName}</p>
                    <p className="text-xs text-muted-foreground capitalize">
                      {resource.role.replace(/_/g, ' ')}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                      <span>{resource.allocationPct}% allocation</span>
                      {resource.billableRate != null && (
                        <span>{formatCurrency(resource.billableRate)}/hr</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Navigation Links */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">Project Resources</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Link
              to={`/projects/tasks?project=${id}`}
              className="flex items-center gap-2 p-3 rounded-lg border hover:bg-muted/50 transition-colors"
            >
              <ListTodo className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Tasks</span>
              <ExternalLink className="h-3 w-3 text-muted-foreground ml-auto" />
            </Link>
            <Link
              to={`/projects/time-entries?project=${id}`}
              className="flex items-center gap-2 p-3 rounded-lg border hover:bg-muted/50 transition-colors"
            >
              <Clock className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Time Entries</span>
              <ExternalLink className="h-3 w-3 text-muted-foreground ml-auto" />
            </Link>
            <Link
              to={`/projects/requirements?project=${id}`}
              className="flex items-center gap-2 p-3 rounded-lg border hover:bg-muted/50 transition-colors"
            >
              <Edit className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Requirements</span>
              <ExternalLink className="h-3 w-3 text-muted-foreground ml-auto" />
            </Link>
            <Link
              to={`/projects/risks?project=${id}`}
              className="flex items-center gap-2 p-3 rounded-lg border hover:bg-muted/50 transition-colors"
            >
              <Shield className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Risks</span>
              <ExternalLink className="h-3 w-3 text-muted-foreground ml-auto" />
            </Link>
          </div>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Project</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{project.name}</strong>? This action cannot be
              undone. All associated tasks, time entries, phases, and other data will be permanently
              removed.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete Project'}
            </Button>
          </DialogFooter>
          {deleteMutation.isError && (
            <p className="text-sm text-destructive mt-2">
              {deleteMutation.error instanceof Error
                ? deleteMutation.error.message
                : 'Failed to delete project'}
            </p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
